import React from 'react';
import { CptCodingGuideArticle } from './cpt-coding-guide';
import { FormularyArticle } from './formulary';
import { PrescribingPolicyArticle } from './prescribing-policy';
import { PsychiatryDocumentationGuideArticle } from './psychiatry-documentation-guide';
import { TherapyDocumentationGuideArticle } from './therapy-documentation-guide';
import { TherapyCptCodesArticle } from './therapy-cpt-codes';
import { createMarkdownArticle } from './markdown-article';
import { content as afterSigningContract } from './content/after-signing-contract';
import { content as cameraChrome } from './content/camera-chrome';
import { content as cameraEdge } from './content/camera-edge';
import { content as cameraSafari } from './content/camera-safari';
import { content as changeAvailability } from './content/change-availability';
import { content as credentialing } from './content/credentialing';
import { content as directDeposit } from './content/direct-deposit';
import { content as directories } from './content/directories';
import { content as exportUpliftCalendar } from './content/export-uplift-calendar';
import { content as getClients } from './content/get-clients';
import { content as getPaid } from './content/get-paid';
import { content as gettingStarted } from './content/getting-started';
import { content as importGoogleCalendar } from './content/import-google-calendar';
import { content as joinASession } from './content/join-a-session';
import { content as manageSessions } from './content/manage-sessions';
import { content as matchingAvailability } from './content/matching-availability';
import { content as matchingProfile } from './content/matching-profile';
import { content as missedSessionsPolicy } from './content/missed-sessions-policy';
import { content as paySchedule } from './content/pay-schedule';
import { content as profilePicture } from './content/profile-picture';
import { content as resetPassword } from './content/reset-password';
import { content as signedContract } from './content/signed-contract';
import { content as upliftTraining } from './content/uplift-training';
import { content as betterhelpRebrandFaq } from './content/betterhelp-rebrand-faq';
import { content as documentationChartsIntro } from './content/documentation-charts-intro';
import { content as telehealthAppropriateness } from './content/telehealth-appropriateness';
import { content as contactPsychiatrist } from './content/contact-psychiatrist';
import { content as dischargeClient } from './content/discharge-client';
import { content as communicationNote } from './content/communication-note';
import { content as progressNotes } from './content/progress-notes';
import { content as documentCenter } from './content/document-center';
import { content as referToPsychiatrist } from './content/refer-to-psychiatrist';
import { content as gad7 } from './content/gad-7';
import { content as pcl5 } from './content/pcl-5';
import { content as phq9 } from './content/phq-9';
import { content as suicideRisk } from './content/suicide-risk';
import { content as reviewPsychiatryNotes } from './content/review-psychiatry-notes';
import { content as chartReviews } from './content/chart-reviews';
import { content as supervisedNps } from './content/supervised-nps';
import { content as supervisionMeeting } from './content/supervision-meeting';
import { content as reactivateClient } from './content/reactivate-client';
import { content as psychiatryServices } from './content/psychiatry-services';
import { content as intakeForms } from './content/intake-forms';
import { content as consentForms } from './content/consent-forms';
import {
  SUPPORT_ARTICLE_CATEGORY_DESCRIPTIONS,
  SUPPORT_ARTICLE_CATEGORY_LABELS,
  SUPPORT_ARTICLE_CATEGORY_ORDER,
  SUPPORT_ARTICLE_SLUGS,
  SupportArticleCategory,
} from './slugs';

export {
  SUPPORT_ARTICLE_CATEGORY_DESCRIPTIONS,
  SUPPORT_ARTICLE_CATEGORY_LABELS,
  SUPPORT_ARTICLE_CATEGORY_ORDER,
  SUPPORT_ARTICLE_SLUGS,
};
export type { SupportArticleCategory, SupportArticleSlug } from './slugs';

export type SupportArticleDefinition = {
  slug: string;
  title: string;
  category: SupportArticleCategory;
  Component: React.ComponentType;
};

export const SUPPORT_ARTICLES: SupportArticleDefinition[] = [
  {
    slug: SUPPORT_ARTICLE_SLUGS.cptCodingGuide,
    title: 'Psychiatry: CPT Code Reference Guide',
    category: 'reference',
    Component: CptCodingGuideArticle,
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.psychiatryDocumentationGuide,
    title: 'Psychiatry - BetterHelp Clinical Documentation Provider Guide',
    category: 'reference',
    Component: PsychiatryDocumentationGuideArticle,
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.therapyDocumentationGuide,
    title: 'Therapy - BetterHelp Clinical Documentation Provider Guide',
    category: 'reference',
    Component: TherapyDocumentationGuideArticle,
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.formulary,
    title: 'BetterHelp Formulary',
    category: 'reference',
    Component: FormularyArticle,
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.prescribingPolicy,
    title: 'Prescribing Policy',
    category: 'reference',
    Component: PrescribingPolicyArticle,
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.gettingStarted,
    title: 'Getting Started Guide',
    category: 'getting-started',
    Component: createMarkdownArticle(gettingStarted),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.joinASession,
    title: 'How do I join a session?',
    category: 'sessions',
    Component: createMarkdownArticle(joinASession),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.manageSessions,
    title: 'How do I manage my sessions on my schedule?',
    category: 'sessions',
    Component: createMarkdownArticle(manageSessions),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.missedSessionsPolicy,
    title: 'My client didn’t show up for our session. What is BetterHelp’s policy for missed sessions or late cancellations?',
    category: 'sessions',
    Component: createMarkdownArticle(missedSessionsPolicy),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.importGoogleCalendar,
    title: 'How to import your Google Calendar',
    category: 'calendar',
    Component: createMarkdownArticle(importGoogleCalendar),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.exportUpliftCalendar,
    title: 'How to export your BetterHelp Calendar',
    category: 'calendar',
    Component: createMarkdownArticle(exportUpliftCalendar),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.matchingAvailability,
    title: 'How do I set up my matching availability?',
    category: 'matching',
    Component: createMarkdownArticle(matchingAvailability),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.changeAvailability,
    title: 'How do I change my availability?',
    category: 'matching',
    Component: createMarkdownArticle(changeAvailability),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.matchingProfile,
    title: 'How do I create and edit my public matching profile?',
    category: 'matching',
    Component: createMarkdownArticle(matchingProfile),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.profilePicture,
    title: 'What photo should I use for my profile picture?',
    category: 'matching',
    Component: createMarkdownArticle(profilePicture),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.getClients,
    title: 'How do I get clients on BetterHelp?',
    category: 'matching',
    Component: createMarkdownArticle(getClients),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.directories,
    title: 'What directories does BetterHelp partner with and how do I get added?',
    category: 'matching',
    Component: createMarkdownArticle(directories),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.getPaid,
    title: 'How do I get paid for sessions?',
    category: 'payments',
    Component: createMarkdownArticle(getPaid),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.directDeposit,
    title: 'How do I set up my direct deposit?',
    category: 'payments',
    Component: createMarkdownArticle(directDeposit),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.paySchedule,
    title: 'When and how often will I get paid?',
    category: 'payments',
    Component: createMarkdownArticle(paySchedule),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.resetPassword,
    title: 'How do I reset my password?',
    category: 'technical',
    Component: createMarkdownArticle(resetPassword),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.cameraChrome,
    title: 'Camera and Mic Permissions for Chrome',
    category: 'technical',
    Component: createMarkdownArticle(cameraChrome),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.cameraSafari,
    title: 'Camera and Mic Permissions for Safari',
    category: 'technical',
    Component: createMarkdownArticle(cameraSafari),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.cameraEdge,
    title: 'Camera and Mic Permissions for Microsoft Edge',
    category: 'technical',
    Component: createMarkdownArticle(cameraEdge),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.afterSigningContract,
    title: 'What should I expect next after signing my BetterHelp contract?',
    category: 'onboarding',
    Component: createMarkdownArticle(afterSigningContract),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.upliftTraining,
    title: 'Where can I find my BetterHelp training?',
    category: 'onboarding',
    Component: createMarkdownArticle(upliftTraining),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.signedContract,
    title: 'How can I find a copy of my signed BetterHelp contract?',
    category: 'onboarding',
    Component: createMarkdownArticle(signedContract),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.credentialing,
    title: 'What is the process for getting credentialed through BetterHelp?',
    category: 'onboarding',
    Component: createMarkdownArticle(credentialing),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.documentationChartsIntro,
    title: "An Intro to BetterHelp's Documentation & Charts",
    category: 'documentation',
    Component: createMarkdownArticle(documentationChartsIntro),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.progressNotes,
    title: 'How do I fill out progress notes?',
    category: 'documentation',
    Component: createMarkdownArticle(progressNotes),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.communicationNote,
    title: 'How do I fill out a communication note?',
    category: 'documentation',
    Component: createMarkdownArticle(communicationNote),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.documentCenter,
    title: 'How do I find and use the Document Center?',
    category: 'documentation',
    Component: createMarkdownArticle(documentCenter),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.intakeForms,
    title: "Where can I find my client's intake forms?",
    category: 'documentation',
    Component: createMarkdownArticle(intakeForms),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.consentForms,
    title: 'Where can I find signed consent forms?',
    category: 'documentation',
    Component: createMarkdownArticle(consentForms),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.dischargeClient,
    title: 'How do I discharge a client?',
    category: 'documentation',
    Component: createMarkdownArticle(dischargeClient),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.reactivateClient,
    title: 'A client I discharged wants to start sessions again. How do I make them an Active Client?',
    category: 'documentation',
    Component: createMarkdownArticle(reactivateClient),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.phq9,
    title: 'How do I use the PHQ-9 for treatment?',
    category: 'assessments',
    Component: createMarkdownArticle(phq9),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.gad7,
    title: 'How do I use the GAD-7 for treatment?',
    category: 'assessments',
    Component: createMarkdownArticle(gad7),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.pcl5,
    title: 'How do I use the PCL-5 for treatment?',
    category: 'assessments',
    Component: createMarkdownArticle(pcl5),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.suicideRisk,
    title: "How do we assess a client's suicide risk?",
    category: 'assessments',
    Component: createMarkdownArticle(suicideRisk),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.psychiatryServices,
    title: 'What psychiatry services does BetterHelp offer?',
    category: 'psychiatry',
    Component: createMarkdownArticle(psychiatryServices),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.referToPsychiatrist,
    title: 'How do I refer a client to a psychiatrist?',
    category: 'psychiatry',
    Component: createMarkdownArticle(referToPsychiatrist),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.contactPsychiatrist,
    title: "How can I contact my client's psychiatry provider?",
    category: 'psychiatry',
    Component: createMarkdownArticle(contactPsychiatrist),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.reviewPsychiatryNotes,
    title: "How can I review a client's notes from their BetterHelp psychiatrist?",
    category: 'psychiatry',
    Component: createMarkdownArticle(reviewPsychiatryNotes),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.chartReviews,
    title: 'How do I complete chart reviews?',
    category: 'psychiatry',
    Component: createMarkdownArticle(chartReviews),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.supervisedNps,
    title: 'How do I view the NPs I supervise?',
    category: 'psychiatry',
    Component: createMarkdownArticle(supervisedNps),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.supervisionMeeting,
    title: 'How do I record a supervision meeting?',
    category: 'psychiatry',
    Component: createMarkdownArticle(supervisionMeeting),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.therapyCptCodes,
    title: 'Therapy - CPT Code Reference Guide',
    category: 'reference',
    Component: TherapyCptCodesArticle,
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.telehealthAppropriateness,
    title: 'Guidelines for Appropriateness of Mental Health Services Via Telehealth',
    category: 'policies',
    Component: createMarkdownArticle(telehealthAppropriateness),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.betterhelpRebrandFaq,
    title: 'BetterHelp Rebrand FAQ',
    category: 'general',
    Component: createMarkdownArticle(betterhelpRebrandFaq),
  },
];

export const getSupportArticleBySlug = (slug: string): SupportArticleDefinition | undefined =>
  SUPPORT_ARTICLES.find(article => article.slug === slug);

export const isSupportArticleCategory = (value: string): value is SupportArticleCategory =>
  SUPPORT_ARTICLE_CATEGORY_ORDER.includes(value as SupportArticleCategory);

export const getSupportArticlesByCategory = (): {
  category: SupportArticleCategory;
  label: string;
  description: string;
  articles: SupportArticleDefinition[];
}[] =>
  SUPPORT_ARTICLE_CATEGORY_ORDER.map(category => ({
    category,
    label: SUPPORT_ARTICLE_CATEGORY_LABELS[category],
    description: SUPPORT_ARTICLE_CATEGORY_DESCRIPTIONS[category],
    articles: SUPPORT_ARTICLES.filter(article => article.category === category),
  })).filter(group => group.articles.length > 0);

export const getSupportCategory = (category: string) =>
  getSupportArticlesByCategory().find(group => group.category === category);
